import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, CardBody, Col, Container, Form, Input, Label, Row, Spinner } from "reactstrap";
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  getAuth,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
} from "firebase/auth";

import porcherMark from "../../assets/images/porcher-logo.png";
import porcherWordmark from "../../assets/images/porcher-wordmark.png";
import { isValidPassword, PASSWORD_REQUIREMENTS } from "../../helpers/passwordValidation";

const AuthAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mode = searchParams.get("mode") || "";
  const oobCode = searchParams.get("oobCode") || "";
  const lang = searchParams.get("lang") || "en";
  const continueUrlParam = searchParams.get("continueUrl") || "";

  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [restoredEmail, setRestoredEmail] = useState("");

  const continueUrl = useMemo(() => {
    if (!continueUrlParam) return "/login";
    try {
      const parsed = new URL(continueUrlParam, window.location.origin);
      if (parsed.origin === window.location.origin) {
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
    } catch {
      return "/login";
    }
    return "/login";
  }, [continueUrlParam]);

  const modeLabel = useMemo(() => {
    if (mode === "verifyEmail") return "Email Verification";
    if (mode === "resetPassword") return "Password Reset";
    if (mode === "recoverEmail") return "Recover Email";
    return "Account Action";
  }, [mode]);

  const auth = getAuth();

  useEffect(() => {
    if (initialized) return;

    const runInit = async () => {
      setInitialized(true);
      setError("");
      setSuccess("");
      auth.languageCode = lang;

      if (!oobCode) {
        setError("This link is missing required verification data.");
        return;
      }

      if (mode === "verifyEmail") {
        setLoading(true);
        try {
          await applyActionCode(auth, oobCode);
          if (auth.currentUser) {
            await auth.currentUser.reload().catch(() => {});
          }
          setSuccess("Email verified successfully. Your account is now active.");
        } catch (err) {
          if (err?.code === "auth/invalid-action-code") {
            setError("This verification link is invalid or has expired. Request a new email verification link.");
          } else {
            setError("We could not verify your email from this link. Please request a new one.");
          }
        } finally {
          setLoading(false);
        }
        return;
      }

      if (mode === "resetPassword") {
        setLoading(true);
        try {
          const resolvedEmail = await verifyPasswordResetCode(auth, oobCode);
          setEmail(resolvedEmail);
        } catch (err) {
          if (err?.code === "auth/expired-action-code" || err?.code === "auth/invalid-action-code") {
            setError("This reset link is invalid or expired. Please request a new password reset email.");
          } else {
            setError("Unable to validate this reset link. Please request a new one.");
          }
        } finally {
          setLoading(false);
        }
        return;
      }

      if (mode === "recoverEmail") {
        setLoading(true);
        try {
          const info = await checkActionCode(auth, oobCode);
          const previousEmail = info?.data?.email || "";

          await applyActionCode(auth, oobCode);
          setRestoredEmail(previousEmail);
          setSuccess("Your email address has been restored. If this wasn't you, reset your password immediately.");

          if (previousEmail) {
            await sendPasswordResetEmail(auth, previousEmail).catch(() => {});
          }
        } catch (err) {
          if (err?.code === "auth/expired-action-code" || err?.code === "auth/invalid-action-code") {
            setError("This recovery link is invalid or expired.");
          } else {
            setError("Unable to recover this email change request.");
          }
        } finally {
          setLoading(false);
        }
        return;
      }

      setError("This action is not supported.");
    };

    void runInit();
  }, [auth, initialized, lang, mode, oobCode]);

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!isValidPassword(password)) {
      setError(PASSWORD_REQUIREMENTS);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess("Password updated successfully. You can now sign in with your new password.");
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      if (err?.code === "auth/expired-action-code" || err?.code === "auth/invalid-action-code") {
        setError("This reset link is invalid or expired. Please request a new password reset email.");
      } else {
        setError("Could not reset your password. Please request a new reset link.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center py-5" style={{ backgroundColor: "#f8f9fa" }}>
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} sm={10} md={8} lg={6} xl={5}>
            <div className="text-center mb-4">
              <Link to="/" className="d-inline-flex align-items-center gap-2">
                <img src={porcherMark} alt="" height="36" />
                <img src={porcherWordmark} alt="Porcher" height="20" />
              </Link>
              <h5 className="mt-3 mb-0 fw-semibold">{modeLabel}</h5>
              <p className="text-muted small">Secure account action</p>
            </div>

            <Card className="border-0 shadow-sm">
              <CardBody className="p-4">
                {loading && (
                  <div className="text-center py-3">
                    <Spinner color="primary" />
                    <p className="text-muted small mt-2 mb-0">Processing your request...</p>
                  </div>
                )}

                {!loading && success && (
                  <Alert color="success" className="d-flex align-items-start gap-2 mb-3">
                    <i className="bx bxs-check-circle font-size-20 mt-1" />
                    <div>
                      <div className="fw-semibold">Success</div>
                      <div>{success}</div>
                    </div>
                  </Alert>
                )}

                {!loading && error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {!loading && mode === "verifyEmail" && (
                  <div className="d-grid gap-2">
                    <Button color="primary" onClick={() => navigate(continueUrl || "/login", { replace: true })}>
                      Continue
                    </Button>
                    <Button color="light" onClick={() => navigate("/login", { replace: true })}>
                      Back to Login
                    </Button>
                  </div>
                )}

                {!loading && mode === "recoverEmail" && (
                  <div className="d-grid gap-2">
                    {restoredEmail && (
                      <Alert color="warning" className="mb-0">
                        Restored account: {restoredEmail}
                      </Alert>
                    )}
                    <Button color="primary" onClick={() => navigate("/forgot-password", { replace: true })}>
                      Reset Password
                    </Button>
                    <Button color="light" onClick={() => navigate("/login", { replace: true })}>
                      Back to Login
                    </Button>
                  </div>
                )}

                {!loading && mode === "resetPassword" && email && !success && (
                  <Form onSubmit={handleResetSubmit}>
                    <div className="mb-3">
                      <Label className="form-label small fw-semibold">Account Email</Label>
                      <Input value={email} disabled />
                    </div>
                    <div className="mb-3">
                      <Label className="form-label small fw-semibold">New Password</Label>
                      <Input
                        type="password"
                        placeholder="Enter new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <div className="form-text">{PASSWORD_REQUIREMENTS}</div>
                    </div>
                    <div className="mb-3">
                      <Label className="form-label small fw-semibold">Confirm Password</Label>
                      <Input
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </div>
                    <Button color="primary" type="submit" className="w-100">
                      Set New Password
                    </Button>
                  </Form>
                )}

                {!loading && mode === "resetPassword" && (success || (error && !email)) && (
                  <div className="d-grid gap-2">
                    <Button color="primary" onClick={() => navigate("/login", { replace: true })}>
                      Go to Login
                    </Button>
                    {success && (
                      <Button color="light" onClick={() => navigate(continueUrl || "/login", { replace: true })}>
                        Continue
                      </Button>
                    )}
                    {error && (
                      <Button color="light" onClick={() => navigate("/forgot-password", { replace: true })}>
                        Request New Reset Link
                      </Button>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
            <p className="text-center text-muted small mt-4 mb-0">
              © {new Date().getFullYear()} Porcher
            </p>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AuthAction;